import { Quaternion, Vector3 } from "three";
import { stringPositionToVector } from "../util/ThreeUtil";
import type { SugarModel } from "./SugarModel";
import { SugarModelBase, SugarModelJointInterface } from "./SugarModelLoader";

export class SugarModelJoint
  extends SugarModelBase
  implements SugarModelJointInterface
{
  private _offset?: Vector3;

  private _direction?: Vector3;
  public model!: SugarModel;

  getModel() {
    return this.model;
  }

  constructor(
    data: SugarModelJointInterface = {
      direction: "0 0 1",
      //up: "0 1 0",
      id: 0,
      inType: "",
      modelId: "0",
      offset: "0 0 0",
      outType: "",
      position: "0 0 0",
    }
  ) {
    super();
    this.userData["info"] = data;
    this.name = this.inType;
  }

  apply(model: SugarModel) {
    const data = this.userData["info"];

    this.removeFromParent();
    this.model = model;

    this._offset = stringPositionToVector(data.offset) || new Vector3();
    let { direction, up, position } = getDirectionOfType(data.inType);

    if (data.direction) {
      direction = stringPositionToVector(data.direction)!;
      position = new Vector3();
    }

    let positionTmp = stringPositionToVector(data.position) || new Vector3();

    if (model) {
      const size = model.localObb.halfSize;
      positionTmp.set(
        position.x * size.x + this._offset.x,
        position.y * size.y * 2 + this._offset.y,
        position.z * size.z + this._offset.z
      );
    }

    this.position.copy(positionTmp);

    this.up.copy(up);
    this._direction = direction;

    const newQuaternion = new Quaternion();
    newQuaternion.setFromUnitVectors(new Vector3(0, 0, 1), direction);
    this.quaternion.copy(newQuaternion);

    model?.root.add(this);
  }

  get direction(): Vector3 {
    return this._direction!;
  }
  get inType(): string {
    return this.userData["info"].inType;
  }
  get modelId(): string {
    return this.userData["info"].modelId;
  }
  get offset(): Vector3 {
    return this._offset!;
  }
  get outType(): string {
    return this.userData["info"].outType;
  }

  get tags(): string[] {
    return this.userData["info"].tags;
  }

  set direction(value: Vector3) {
    this._direction = value;
    this.userData["info"].direction = value
      .toArray()
      .map((e) => e.toFixed(2))
      .join(" ");
  }

  set inType(value: string) {
    this.userData["info"].inType = value;
  }

  set modelId(value: string) {
    this.userData["info"].modelId = value;
  }

  set offset(value: Vector3) {
    this._offset = value;
    this.userData["info"].offset = value
      .toArray()
      .map((e) => e.toFixed(4))
      .join(" ");
  }

  set outType(value: string) {
    this.userData["info"].outType = value;
  }

  get jointId() {
    return this.userData["info"].id;
  }

  private _bindedJoint?: SugarModelJoint;

  bindJoint(other: SugarModelJoint) {
    other._bindedJoint = this;
    this._bindedJoint = other;
    this.dispatchEvent({
      type: "binded" as any,
      detail: { joints: [this, other] },
    });
  }

  private _attached = false;
  attachJoint(other: SugarModelJoint) {
    this.bindJoint(other);
    this._attached = true;
  }

  unbindJoint() {
    const bindedJoint = this._bindedJoint;
    this._bindedJoint = undefined;
    bindedJoint?.unbindJoint();
    this._attached = false;
  }

  get bindedJoint() {
    return this._bindedJoint;
  }

  get binded() {
    return this._bindedJoint ? true : false;
  }

  set visibility(value: boolean) {
    this.visible = value;
    if (this._bindedJoint) {
      this._bindedJoint.visible = value;
      this._bindedJoint.model.visible = value;
    }
  }

  get visibility() {
    return this.visible;
  }

  resetVisibility() {
    this.visibility = this.visible;
  }
}

const getDirectionOfType = (
  type: string
): { direction: Vector3; up: Vector3; position: Vector3 } => {
  if (type === "sag") {
    return {
      direction: new Vector3(1.0, 0, 0),
      up: new Vector3(0, 1, 0),
      position: new Vector3(1, 0, -1),
    };
  }
  if (type === "sol") {
    return {
      direction: new Vector3(-1.0, 0, 0),
      up: new Vector3(0, 1, 0),
      position: new Vector3(-1, 0, -1),
    };
  }
  if (type === "arka") {
    return {
      direction: new Vector3(0.0, 0, -1),
      up: new Vector3(0, 1, 0),
      position: new Vector3(0, 0, -1),
    };
  }
  if (type === "on") {
    return {
      direction: new Vector3(0, 0, 1),
      up: new Vector3(0, 1, 0),
      position: new Vector3(0, 0, 1),
    };
  }
  if (type === "top") {
    return {
      direction: new Vector3(0, 1, 0),
      up: new Vector3(0, 0, 1),
      position: new Vector3(0, 1, 0),
    };
  }
  if (type === "bottom") {
    return {
      direction: new Vector3(0, -1, 0),
      up: new Vector3(0, 0, -1),
      position: new Vector3(0, 0, 0),
    };
  }
  return {
    direction: new Vector3(0.0, 0, 1.0),
    up: new Vector3(0, 1, 0),
    position: new Vector3(0, 0, 0),
  };
};
